import React from 'react';
import { Box, Container, Typography, Card, CardContent, Button, useTheme, alpha, Stack } from '@mui/material';
import { CheckCircle, Psychology, MonetizationOn, CalendarMonth, AutoAwesome } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';

const FeaturesSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const features = [
    {
      icon: <CalendarMonth sx={{ fontSize: 32 }} />,
      title: 'Built for High Volume',
      description: 'Precision scheduling with conflict detection, series bookings, and live waitlists. Not a widget — a real engine.',
      gridArea: { md: '1 / 1 / 3 / 3' },
      color: theme.palette.primary.main
    },
    {
      icon: <Psychology sx={{ fontSize: 28 }} />,
      title: 'Predictive CRM',
      description: 'Spots churn risk 30 days early.',
      gridArea: { md: '1 / 3 / 2 / 5' },
      color: theme.palette.secondary.main
    },
    {
      icon: <MonetizationOn sx={{ fontSize: 28 }} />,
      title: 'Revenue Guard',
      description: 'Automated billing & collection.',
      gridArea: { md: '2 / 3 / 3 / 4' },
      color: theme.palette.success.main
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 28 }} />,
      title: 'AI Manager',
      description: 'Auto-replies & insights.',
      gridArea: { md: '2 / 4 / 3 / 5' },
      color: theme.palette.info.main
    }
  ];

  return (
    <Box sx={{
      py: { xs: 10, md: 15 },
      background: '#000000',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                letterSpacing: 2,
                mb: 2,
                display: 'block'
              }}
            >
              Why FutureSense
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.04em',
                lineHeight: 1
              }}
            >
              One app instead of five.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 6,
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
                color: theme.palette.text.secondary,
                lineHeight: 1.6
              }}
            >
              Stop stitching together Calendly, QuickBooks, and spreadsheets. 
              FutureSense handles the entire operational lifecycle in one place.
            </Typography>
          </motion.div>
        </Box>

        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gridTemplateRows: { xs: 'auto', md: 'repeat(2, 1fr)' },
            gap: 3,
            mb: 8
          }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ gridArea: feature.gridArea.md }}
            >
              <Card
                sx={{
                  height: '100%',
                  ...theme.customEffects?.glass,
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: alpha(feature.color, 0.3),
                    background: alpha(feature.color, 0.02),
                  }
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: feature.gridArea.md.includes('1 / 1') ? 6 : 3,
                    color: feature.color,
                    background: alpha(feature.color, 0.1),
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#ffffff', letterSpacing: '-0.02em' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>
              </Card>
            </motion.div>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(ROUTES.SIGNUP)}
            sx={{ px: 6, py: 1.8 }}
          >
            Start Your Practice Free
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
